/**
 * Tree Transform Functions
 * Transforms API responses to frontend tree data
 */

import type { PartsTreeRowAPI } from "../api/types";
import type { PartInfo, EquipmentStatus } from "../../../../shared/models";
import type { RoofTreeRow } from "../frontend/types";
import {
  PART_TYPE_IDS,
  PART_IDS,
  CONTAINER_NAMES,
  SPARES_CONTAINER_NAMES,
  SPARES_CONTAINER_ORDER,
} from "../constants/parts";
import {
  createContainerRow,
  buildSparePartsMap,
  sortAndDeduplicatePositions,
} from "../../../../shared/utils/tree-transform-helpers";

// ============================================
// Types for Internal Use
// ============================================

interface RoofInfo {
  name: string;
  status: EquipmentStatus;
  parentId: number;
  campaignValue: number;
  totalValue: number;
  initialAbsoluteValue: number;
  lastTaskTimestamp: Date | null;
  lastTaskId: number | null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build a map of roof IDs to roof info from API data
 */
const buildRoofInfoMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, RoofInfo> => {
  const roofInfoMap = new Map<number, RoofInfo>();

  for (const row of apiData) {
    if (row.L1PartTypeId === PART_TYPE_IDS.ROOF && row.L2PartId) {
      const isOnline = row.L1PartId === PART_IDS.EAF_ONLINE;
      roofInfoMap.set(row.L2PartId, {
        name: row.L2PartName || `Roof #${row.L2PartId}`,
        status: isOnline ? "Online" : "Offline",
        parentId: row.L1PartId,
        campaignValue: row.CampaignValue || 0,
        totalValue: row.TotalValue || 0,
        initialAbsoluteValue: row.InitialAbsoluteValue || 0,
        lastTaskTimestamp: row.LastTaskTimestamp ?? null,
        lastTaskId: row.LastTaskId ?? null,
      });
    }
  }

  return roofInfoMap;
};

/**
 * Build a map of roof IDs to their panel positions
 */
const buildRoofPositionsMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, PartsTreeRowAPI[]> => {
  const roofPositionsMap = new Map<number, PartsTreeRowAPI[]>();

  // Filter panel rows (types 101-112)
  const panelRows = apiData.filter(
    (row) =>
      row.L1PartTypeId >= PART_TYPE_IDS.SPIDER &&
      row.L1PartTypeId <= PART_TYPE_IDS.ELBOW_SHIELD,
  );

  for (const row of panelRows) {
    const roofId = row.L1PartId;
    const positions = roofPositionsMap.get(roofId);
    if (positions) {
      positions.push(row);
    } else {
      roofPositionsMap.set(roofId, [row]);
    }
  }

  return roofPositionsMap;
};

/**
 * Separate roofs by online/offline status
 */
const separateRoofsByStatus = (
  roofInfoMap: Map<number, RoofInfo>,
): { onlineRoofs: number[]; offlineRoofs: number[] } => {
  const onlineRoofs: number[] = [];
  const offlineRoofs: number[] = [];

  for (const [roofId, info] of roofInfoMap) {
    if (info.status === "Online") {
      onlineRoofs.push(roofId);
    } else {
      offlineRoofs.push(roofId);
    }
  }

  return { onlineRoofs, offlineRoofs };
};

/**
 * Create an equipment row for a roof
 */
const createEquipmentRow = (
  roofId: number,
  roofInfo: RoofInfo,
  basePath: string[],
): RoofTreeRow => ({
  id: `equipment-${roofId}`,
  path: [...basePath, roofInfo.name],
  rowType: "equipment",
  EquipmentId: roofId,
  EquipmentName: roofInfo.name,
  Status: roofInfo.status,
  InstalledDate: roofInfo.lastTaskTimestamp,
  LastTaskId: roofInfo.lastTaskId,
  Heats: roofInfo.campaignValue,
  TotalHeats: roofInfo.totalValue,
  InitialAbsoluteValue: roofInfo.initialAbsoluteValue,
  Info: roofInfo.status,
  HasChildren: true,
});

/**
 * Create a location row for a panel position
 */
const createLocationRow = (
  roofId: number,
  roofInfo: RoofInfo,
  position: PartsTreeRowAPI,
  roofPath: string[],
): RoofTreeRow => {
  const hasPart = position.L2PartId !== null;
  const partInfo: PartInfo | null = hasPart
    ? {
        Make: position.Make || "",
        Model: position.Model || "",
        Vendor: position.Vendor || "",
        SerialNumber: position.SerialNumber || "",
      }
    : null;

  return {
    id: `location-${roofId}-${position.L1PositionId}`,
    path: [...roofPath, position.L1PositionName],
    rowType: "location",
    EquipmentId: roofId,
    EquipmentName: roofInfo.name,
    Status: roofInfo.status,
    PositionId: position.L1PositionId,
    PositionName: position.L1PositionName,
    PartTypeId: position.L1PartTypeId,
    L2PartId: position.L2PartId,
    L2PartName: position.L2PartName,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (position.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (position.PartialValue ?? null) : null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: hasPart
      ? [position.Make, position.Model].filter(Boolean).join(" ") || "-"
      : null,
    Comments: position.Comments,
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
  };
};

/**
 * Create a spare part row
 */
const createSparePartRow = (
  containerId: number,
  containerName: string,
  part: PartsTreeRowAPI,
  containerPath: string[],
): RoofTreeRow => {
  const partInfo: PartInfo = {
    Make: part.Make || "",
    Model: part.Model || "",
    Vendor: part.Vendor || "",
    SerialNumber: part.SerialNumber || "",
  };

  const partDisplayName =
    [part.SerialNumber, part.Make, part.Model].filter(Boolean).join(" ") || "-";

  // Append PartId to make path unique when display names are identical
  const uniquePathName = `${partDisplayName} (#${part.L2PartId})`;

  return {
    id: `spare-${containerId}-${part.L2PartId}`,
    path: [...containerPath, uniquePathName],
    rowType: "sparePart",
    ContainerId: containerId,
    ContainerName: containerName,
    EquipmentId: containerId,
    L2PartId: part.L2PartId,
    L2PartName: part.L2PartName,
    PartInfo: partInfo,
    PartTypeId: part.L1PartTypeId,
    TypeIdentity: part.IAm
      ? part.IAm.split(",").map(Number)
      : [part.L1PartTypeId],
    Heats: part.CampaignValue ?? null,
    HeatsSinceRepair: part.PartialValue ?? null,
    TotalHeats: part.TotalValue || 0,
    InitialAbsoluteValue: part.InitialAbsoluteValue || 0,
    StatusCode: part.StatusCode,
    InstalledDate: part.LastTaskTimestamp ?? null,
    LastTaskId: part.LastTaskId ?? null,
    HasAssignedPart: true,
    IsReplaceable: part.IsReplaceable ?? undefined,
    IsArray: part.IsArray ?? undefined,
    SortOrder: part.SortOrder ?? undefined,
  };
};

/**
 * Add a roof and its panels to the result array
 */
const addRoofWithPanels = (
  result: RoofTreeRow[],
  roofId: number,
  roofInfo: RoofInfo,
  basePath: string[],
  roofPositionsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  const roofPath = [...basePath, roofInfo.name];

  // Add Equipment row (Roof)
  result.push(createEquipmentRow(roofId, roofInfo, basePath));

  // Add panel positions
  const positions = roofPositionsMap.get(roofId) || [];

  // Sort positions by L1PositionId, deduplicate
  const sortedPositions = sortAndDeduplicatePositions(positions);

  for (const pos of sortedPositions) {
    result.push(createLocationRow(roofId, roofInfo, pos, roofPath));
  }
};

/**
 * Add spares containers and their spare parts to the result array
 */
const addSparesContainers = (
  result: RoofTreeRow[],
  roofPartsSparesPath: string[],
  sparePartsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  for (const containerId of SPARES_CONTAINER_ORDER) {
    const containerName = SPARES_CONTAINER_NAMES[containerId];
    if (!containerName) continue; // Skip if mapping not found
    const containerPath = [...roofPartsSparesPath, containerName];

    // Add the spares container row
    result.push(
      createContainerRow<RoofTreeRow>(
        containerId,
        containerName,
        containerPath,
        "sparesContainer",
      ),
    );

    // Add spare parts in this container
    const spareParts = sparePartsMap.get(containerId) || [];
    for (const part of spareParts) {
      if (!part.L2PartId) continue;
      result.push(
        createSparePartRow(containerId, containerName, part, containerPath),
      );
    }
  }
};

// ============================================
// Main Transform Function
// ============================================

/**
 * Transform API response from GetPartsTree to frontend RoofTreeRow[]
 *
 * Creates full hierarchy:
 * - EAF-Online
 *   - Roof #X (online)
 *     - panels...
 * - EAF-Offline
 *   - Roof Spares
 *     - Roof #Y (offline)
 *       - panels...
 *   - Roof Parts Spares
 *     - Spider Spares
 *       - spare panels...
 *
 * @param apiData - Raw rows from EQP.GetPartsTree
 * @returns Flattened tree data for ag-Grid
 */
export const transformAPIToTreeData = (
  apiData: PartsTreeRowAPI[],
): RoofTreeRow[] => {
  const result: RoofTreeRow[] = [];

  // Step 1: Build lookup maps
  const roofInfoMap = buildRoofInfoMap(apiData);
  const roofPositionsMap = buildRoofPositionsMap(apiData);
  const sparesContainerIds = Object.keys(SPARES_CONTAINER_NAMES).map(Number);
  const sparePartsMap = buildSparePartsMap(apiData, sparesContainerIds);

  // Step 2: Separate roofs by status
  const { onlineRoofs, offlineRoofs } = separateRoofsByStatus(roofInfoMap);

  // ==========================================
  // BUILD THE HIERARCHY
  // ==========================================

  // 1. EAF-Online container
  const eafOnlinePath = [CONTAINER_NAMES.EAF_ONLINE];
  result.push(
    createContainerRow<RoofTreeRow>(
      PART_IDS.EAF_ONLINE,
      CONTAINER_NAMES.EAF_ONLINE,
      eafOnlinePath,
      "container",
    ),
  );

  // Add online roofs under EAF-Online
  for (const roofId of onlineRoofs) {
    const roofInfo = roofInfoMap.get(roofId);
    if (roofInfo) {
      addRoofWithPanels(
        result,
        roofId,
        roofInfo,
        eafOnlinePath,
        roofPositionsMap,
      );
    }
  }

  // 2. EAF-Offline container
  const eafOfflinePath = [CONTAINER_NAMES.EAF_OFFLINE];
  result.push(
    createContainerRow<RoofTreeRow>(
      PART_IDS.EAF_OFFLINE,
      CONTAINER_NAMES.EAF_OFFLINE,
      eafOfflinePath,
      "container",
    ),
  );

  // 2a. Roof Spares container (contains offline roofs)
  const roofSparesPath = [...eafOfflinePath, CONTAINER_NAMES.ROOF_SPARES];
  result.push(
    createContainerRow<RoofTreeRow>(
      PART_IDS.ROOF_SPARES,
      CONTAINER_NAMES.ROOF_SPARES,
      roofSparesPath,
      "sparesContainer",
    ),
  );

  // Add offline roofs under Roof Spares
  for (const roofId of offlineRoofs) {
    const roofInfo = roofInfoMap.get(roofId);
    if (roofInfo) {
      addRoofWithPanels(
        result,
        roofId,
        roofInfo,
        roofSparesPath,
        roofPositionsMap,
      );
    }
  }

  // 2b. Roof Parts Spares container (contains panel spares by type)
  const roofPartsSparesPath = [
    ...eafOfflinePath,
    CONTAINER_NAMES.ROOF_PARTS_SPARES,
  ];
  result.push(
    createContainerRow<RoofTreeRow>(
      PART_IDS.ROOF_PARTS_SPARES,
      CONTAINER_NAMES.ROOF_PARTS_SPARES,
      roofPartsSparesPath,
      "sparesContainer",
    ),
  );

  // Add spares containers under Roof Parts Spares
  addSparesContainers(result, roofPartsSparesPath, sparePartsMap);

  return result;
};
